// Formulário público de simulação de troca
// Atualizado para MUI v7 com Grid v3 (Grid2) e size={{ xs: 12, md: 5 }}
// Visual aprimorado com ícones e CTA simples.
// Padrão: react-hook-form + connect/bindActionCreators + styled-components

import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  InputAdornment,
  Grid,
} from '@mui/material';

import {
  PersonRounded,
  PhoneRounded,
  EmailRounded,
  PhoneIphoneRounded,
  SdStorageRounded,
  PaletteRounded,
  BatteryCharging90Rounded,
  ReportProblemRounded,
  BuildRounded,
  PaymentsRounded,
  SendRounded,
  StarRounded,
} from '@mui/icons-material';

import { Controller, useForm } from 'react-hook-form';
import { Creators as TradeInSimulationsCreators } from 'store/trade-in-simulations/duck.js';
import * as S from './styled';

// Validação simples BR: 10–13 dígitos (com/sem DDI)
const isValidBrazilPhone = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  return [10, 11, 12, 13].includes(digits.length);
};
const normalizePhone = (v) => String(v || '').replace(/\D/g, '');

const TradeInSimulationFormPage = ({ tradeInSimulationsCreateAsync }) => {
  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      fullName: '',
      phoneNumber: '',
      email: '',
      device: '',
      capacity: '',
      color: '',
      batteryHealth: '',
      usageOrBroken: '',
      malfunctions: '',
      interestedIphone: '',
      paymentMethod: '', // 'avista' | 'cartao'
    },
    mode: 'onBlur',
  });

  const onSubmit = useCallback(
    async (values) => {
      const payload = { ...values, phoneNumber: normalizePhone(values.phoneNumber) };
      await tradeInSimulationsCreateAsync(payload);
      // Se quiser limpar após sucesso, descomente:
      // reset();
    },
    [tradeInSimulationsCreateAsync]
  );

  return (
    <S.PageContainer>
      <Card sx={{ maxWidth: 920, width: '100%', borderRadius: '20px', boxShadow: 10 }}>
        <CardHeader
          title={
            <S.TitleRow>
              <Typography variant="h5" component="div" sx={{ fontWeight: 700 }}>
                Formulário para simulação de troca
              </Typography>
            </S.TitleRow>
          }
        />
        <CardContent>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Grid container spacing={2} columns={{ xs: 12, md: 10 }}>
              {/* Nome */}
              <Grid size={{ xs: 12, md: 5 }}>
                <TextField
                  label="Nome completo"
                  placeholder="Ex.: João da Silva"
                  fullWidth
                  required
                  autoComplete="name"
                  {...register('fullName', {
                    required: 'Informe seu nome completo.',
                    minLength: { value: 3, message: 'Mínimo de 3 caracteres.' },
                  })}
                  error={!!errors.fullName}
                  helperText={errors.fullName?.message}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonRounded />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <TextField
                  label="Número de telefone"
                  placeholder="(49) 9 9999-9999"
                  fullWidth
                  required
                  autoComplete="tel"
                  {...register('phoneNumber', {
                    required: 'Informe seu número de telefone.',
                    validate: (v) => isValidBrazilPhone(v) || 'Número inválido.',
                  })}
                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber?.message}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneRounded />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <TextField
                  label="E-mail"
                  placeholder="voce@email.com"
                  type="email"
                  fullWidth
                  autoComplete="email"
                  {...register('email', {
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/i,
                      message: 'E-mail inválido.',
                    },
                  })}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailRounded />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <TextField
                  label="Aparelho que vai dar na troca"
                  placeholder="Ex.: iPhone 12"
                  fullWidth
                  required
                  {...register('device', {
                    required: 'Informe o aparelho.',
                  })}
                  error={!!errors.device}
                  helperText={errors.device?.message}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIphoneRounded />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid>
              {/* iPhone de interesse (NOVO) */}
              <Grid size={{ xs: 12, md: 5 }}>
                <TextField
                  label="iPhone de interesse"
                  placeholder="Ex.: iPhone 14 Pro / 15 / 15 Pro Max"
                  fullWidth
                  {...register('interestedIphone')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <StarRounded />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <TextField
                  label="Capacidade (GB)"
                  placeholder="Ex.: 128GB"
                  fullWidth
                  {...register('capacity')}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SdStorageRounded />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <TextField
                  label="Cor do aparelho"
                  placeholder="Ex.: Preto"
                  fullWidth
                  {...register('color')}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <PaletteRounded />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <TextField
                  label="Saúde da bateria"
                  placeholder="Ex.: 89%"
                  fullWidth
                  {...register('batteryHealth')}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <BatteryCharging90Rounded />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="O aparelho tem marcas de uso ou está quebrado?"
                  placeholder="Descreva riscos, trincos, amassados etc."
                  fullWidth
                  multiline
                  minRows={2}
                  {...register('usageOrBroken')}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <ReportProblemRounded />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Algo não funciona corretamente? (som, câmera, microfone, tela etc.)"
                  placeholder="Descreva os problemas"
                  fullWidth
                  multiline
                  minRows={2}
                  {...register('malfunctions')}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <BuildRounded />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid>

              {/* Método de pagamento */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                  <PaymentsRounded fontSize="small" />
                  Método de pagamento
                </Typography>
                <Controller
                  name="paymentMethod"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup row {...field}>
                      <FormControlLabel value="avista" control={<Radio />} label="À vista" />
                      <FormControlLabel value="cartao" control={<Radio />} label="Cartão" />
                    </RadioGroup>
                  )}
                />
              </Grid>

              {/* CTA */}
              <Grid size={{ xs: 12 }}>
                <S.Actions>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    endIcon={<SendRounded />}
                  >
                    Enviar simulação
                  </Button>
                </S.Actions>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </S.PageContainer>
  );
};
const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(TradeInSimulationsCreators, dispatch);

TradeInSimulationFormPage.propTypes = {
  tradeInSimulationsCreateAsync: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(TradeInSimulationFormPage);
